import { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { tenantsAPI, unitsAPI, propertiesAPI } from '../lib/api';
import { formatDate, formatCurrency } from '../lib/utils';
import { toast } from 'sonner';
import {
  Plus,
  Users,
  Edit,
  Trash2,
  Loader2,
  Search,
  Home,
  Calendar,
  Euro
} from 'lucide-react';

export default function Tenants() {
  const [tenants, setTenants] = useState([]);
  const [units, setUnits] = useState([]);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingTenant, setEditingTenant] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [formData, setFormData] = useState({
    unit_id: '',
    name: '',
    email: '',
    phone: '',
    move_in_date: '',
    move_out_date: '',
    monthly_prepayment: '',
    person_count: 1
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [tenantsRes, unitsRes, propsRes] = await Promise.all([
        tenantsAPI.getAll(),
        unitsAPI.getAll(),
        propertiesAPI.getAll()
      ]);
      setTenants(tenantsRes.data);
      setUnits(unitsRes.data);
      setProperties(propsRes.data);
    } catch (error) {
      toast.error('Fehler beim Laden');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      unit_id: units.length > 0 ? units[0].id : '',
      name: '',
      email: '',
      phone: '',
      move_in_date: '',
      move_out_date: '',
      monthly_prepayment: '',
      person_count: 1
    });
    setEditingTenant(null);
  };

  const openEditDialog = (tenant) => {
    setEditingTenant(tenant);
    setFormData({
      unit_id: tenant.unit_id,
      name: tenant.name,
      email: tenant.email || '',
      phone: tenant.phone || '',
      move_in_date: tenant.move_in_date?.slice(0, 10) || '',
      move_out_date: tenant.move_out_date?.slice(0, 10) || '',
      monthly_prepayment: tenant.monthly_prepayment || '',
      person_count: tenant.person_count
    });
    setIsDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const data = {
        ...formData,
        email: formData.email || null,
        phone: formData.phone || null,
        move_out_date: formData.move_out_date || null,
        monthly_prepayment: formData.monthly_prepayment ? parseFloat(formData.monthly_prepayment) : 0,
        person_count: parseInt(formData.person_count)
      };

      if (editingTenant) {
        await tenantsAPI.update(editingTenant.id, data);
        toast.success('Mieter aktualisiert');
      } else {
        await tenantsAPI.create(data);
        toast.success('Mieter erstellt');
      }

      setIsDialogOpen(false);
      resetForm();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;

    try {
      await tenantsAPI.delete(deleteId);
      toast.success('Mieter gelöscht');
      loadData();
    } catch (error) {
      toast.error('Fehler beim Löschen');
    } finally {
      setDeleteId(null);
    }
  };

  const getUnitInfo = (unitId) => {
    const unit = units.find(u => u.id === unitId);
    if (!unit) return { name: 'Unbekannt', property: 'Unbekannt' };
    const property = properties.find(p => p.id === unit.property_id);
    return {
      name: unit.name,
      property: property ? property.name : 'Unbekannt'
    };
  };

  const filteredTenants = tenants.filter(t => {
    const matchesSearch = t.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (t.email && t.email.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesStatus = filterStatus === 'all' || 
      (filterStatus === 'active' && t.is_active) ||
      (filterStatus === 'inactive' && !t.is_active);
    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div data-testid="tenants-page" className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Mieter
            </h1>
            <p className="text-slate-500 mt-1">Verwalten Sie Ihre Mietverhältnisse</p>
          </div>
          <Dialog open={isDialogOpen} onOpenChange={(open) => {
            setIsDialogOpen(open);
            if (!open) resetForm();
          }}>
            <DialogTrigger asChild>
              <Button 
                data-testid="add-tenant-button" 
                className="bg-slate-900 hover:bg-slate-800"
                disabled={units.length === 0}
              >
                <Plus className="mr-2 h-4 w-4" />
                Mieter hinzufügen
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[500px]">
              <DialogHeader>
                <DialogTitle>
                  {editingTenant ? 'Mieter bearbeiten' : 'Neuer Mieter'}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label htmlFor="unit_id">
                    Einheit <span className="text-red-500">*</span>
                  </Label>
                  <Select
                    value={formData.unit_id}
                    onValueChange={(value) => setFormData({ ...formData, unit_id: value })}
                    required
                  >
                    <SelectTrigger data-testid="tenant-unit-select">
                      <SelectValue placeholder="Einheit auswählen" />
                    </SelectTrigger>
                    <SelectContent>
                      {units.map((unit) => {
                        const prop = properties.find(p => p.id === unit.property_id);
                        return (
                          <SelectItem key={unit.id} value={unit.id}>
                            {unit.name} ({prop?.name})
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2 space-y-2">
                    <Label htmlFor="name">
                      Name <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="name"
                      data-testid="tenant-name-input"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="Max Mustermann"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email">E-Mail</Label>
                    <Input
                      id="email"
                      type="email"
                      data-testid="tenant-email-input"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="mieter@email.de"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone">Telefon</Label>
                    <Input
                      id="phone"
                      data-testid="tenant-phone-input"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      placeholder="+49 123 456789"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="move_in_date">
                      Einzugsdatum <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="move_in_date"
                      type="date"
                      data-testid="tenant-move-in-input"
                      value={formData.move_in_date}
                      onChange={(e) => setFormData({ ...formData, move_in_date: e.target.value })}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="move_out_date">Auszugsdatum</Label>
                    <Input
                      id="move_out_date"
                      type="date"
                      data-testid="tenant-move-out-input"
                      value={formData.move_out_date}
                      onChange={(e) => setFormData({ ...formData, move_out_date: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="monthly_prepayment">
                      NK-Vorauszahlung/Monat (€)
                    </Label>
                    <Input
                      id="monthly_prepayment"
                      type="number"
                      step="0.01"
                      data-testid="tenant-prepayment-input"
                      value={formData.monthly_prepayment}
                      onChange={(e) => setFormData({ ...formData, monthly_prepayment: e.target.value })}
                      placeholder="150.00"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="person_count">
                      Personenanzahl <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="person_count"
                      type="number"
                      min="1"
                      data-testid="tenant-persons-input"
                      value={formData.person_count}
                      onChange={(e) => setFormData({ ...formData, person_count: e.target.value })}
                      required
                    />
                  </div>
                </div>

                <div className="flex justify-end gap-3 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setIsDialogOpen(false)}
                  >
                    Abbrechen
                  </Button>
                  <Button type="submit" disabled={saving} data-testid="tenant-save-button">
                    {saving ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Speichern...
                      </>
                    ) : (
                      'Speichern'
                    )}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              data-testid="tenant-search-input"
              placeholder="Suchen nach Name oder E-Mail..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-full sm:w-[180px]" data-testid="tenant-filter-status">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle Status</SelectItem>
              <SelectItem value="active">Aktiv</SelectItem>
              <SelectItem value="inactive">Ausgezogen</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* No Units Warning */}
        {units.length === 0 && (
          <Card className="border-amber-200 bg-amber-50">
            <CardContent className="py-4">
              <p className="text-amber-800 text-sm">
                <Home className="inline h-4 w-4 mr-2" />
                Erstellen Sie zuerst Einheiten, um Mieter hinzufügen zu können.
              </p>
            </CardContent>
          </Card>
        )}

        {/* Tenants List */}
        {filteredTenants.length > 0 ? (
          <Card className="border-slate-200">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50/50">
                  <TableHead className="font-semibold">Mieter</TableHead>
                  <TableHead className="font-semibold">Einheit</TableHead>
                  <TableHead className="font-semibold">Mietdauer</TableHead>
                  <TableHead className="font-semibold text-right">NK-Vorauszahlung</TableHead>
                  <TableHead className="font-semibold text-center">Personen</TableHead>
                  <TableHead className="font-semibold">Status</TableHead>
                  <TableHead className="w-[100px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredTenants.map((tenant) => {
                  const unitInfo = getUnitInfo(tenant.unit_id);
                  return (
                    <TableRow key={tenant.id} data-testid={`tenant-row-${tenant.id}`}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-violet-100 flex items-center justify-center text-sm font-medium text-violet-700">
                            {tenant.name.charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <p className="font-medium text-slate-900">{tenant.name}</p>
                            {tenant.email && (
                              <p className="text-sm text-slate-500">{tenant.email}</p>
                            )}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="text-slate-700">{unitInfo.name}</p>
                          <p className="text-sm text-slate-500">{unitInfo.property}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2 text-sm">
                          <Calendar className="h-4 w-4 text-slate-400" />
                          <span>
                            {formatDate(tenant.move_in_date)}
                            {tenant.move_out_date && (
                              <> - {formatDate(tenant.move_out_date)}</>
                            )}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1 tabular-nums font-medium">
                          <Euro className="h-3 w-3 text-slate-400" />
                          {tenant.monthly_prepayment > 0 
                            ? `${tenant.monthly_prepayment.toFixed(2)}/mtl.`
                            : '-'
                          }
                        </div>
                      </TableCell>
                      <TableCell className="text-center tabular-nums">
                        {tenant.person_count}
                      </TableCell>
                      <TableCell>
                        <Badge 
                          variant={tenant.is_active ? 'default' : 'secondary'}
                          className={tenant.is_active 
                            ? 'bg-emerald-100 text-emerald-800 hover:bg-emerald-100' 
                            : 'bg-slate-100 text-slate-600'
                          }
                        >
                          {tenant.is_active ? 'Aktiv' : 'Ausgezogen'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            data-testid={`edit-tenant-${tenant.id}`}
                            onClick={() => openEditDialog(tenant)}
                          >
                            <Edit className="h-4 w-4 text-slate-500" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            data-testid={`delete-tenant-${tenant.id}`}
                            onClick={() => setDeleteId(tenant.id)}
                          >
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Card>
        ) : (
          <Card className="border-slate-200">
            <CardContent className="py-12 text-center">
              <Users className="h-12 w-12 mx-auto mb-4 text-slate-300" />
              <h3 className="text-lg font-medium text-slate-900 mb-2">
                {searchTerm || filterStatus !== 'all' ? 'Keine Mieter gefunden' : 'Noch keine Mieter'}
              </h3>
              <p className="text-slate-500 mb-4">
                {searchTerm || filterStatus !== 'all'
                  ? 'Versuchen Sie andere Filtereinstellungen' 
                  : 'Fügen Sie Ihren ersten Mieter hinzu'
                }
              </p>
              {!searchTerm && filterStatus === 'all' && units.length > 0 && (
                <Button onClick={() => setIsDialogOpen(true)} data-testid="empty-add-tenant-button">
                  <Plus className="mr-2 h-4 w-4" />
                  Ersten Mieter hinzufügen
                </Button>
              )}
            </CardContent>
          </Card>
        )}

        {/* Delete Confirmation */}
        <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Mieter löschen?</AlertDialogTitle>
              <AlertDialogDescription>
                Diese Aktion kann nicht rückgängig gemacht werden. Das Mietverhältnis 
                wird dauerhaft entfernt.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Abbrechen</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDelete}
                className="bg-red-600 hover:bg-red-700"
                data-testid="confirm-delete-tenant"
              >
                Löschen
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </Layout>
  );
}
